import { get, isEmpty } from "lodash";
import { InvestorRequestState, InvestorRequestType } from "@investorRequest/models";
import FeatureToggleService from "@services/FeatureToggleService";
import { ToggledFeature } from "@shared/models/featureToggle";

// Regexes per requirements
export const NUMERIC_ACCOUNT_REGEX = /^\d{9,18}$/;
// Alphanumeric accounts must include at least one letter, 6–10 total chars
export const ALPHANUMERIC_ACCOUNT_REGEX = /^(?=.*[A-Za-z])[0-9A-Za-z]{6,10}$/;
// Optional separators pattern per spec: exactly 3 digits, optional single char, a whitespace, then 1 digit
export const OPTIONAL_SEPARATORS_ACCOUNT_REGEX = /^\d{3}.?\s\d$/;
export const ABA_REGEX = /^\d{9}$/;
export const ROUTING_REGEX = /^\d{9}$/;
// External routing: alphanumeric, max 15 characters OR exactly 9 digits
export const EXTERNAL_ROUTING_REGEX = /^[A-Za-z0-9]{1,15}$/;
// Allow only English letters, digits, spaces and dash
export const ASCII_NAME_REGEX = /^[A-Za-z0-9\s]+$/;

// IR-UI-Revamp_V1: SWIFT field validation regexes
export const SWIFT_ID_REGEX = /^[A-Za-z0-9]{1,11}$/;
export const IBAN_REGEX = /^[A-Za-z0-9]{1,34}$/;
export const BANK_ID_REGEX = /^\d{1,20}$/;
export const BRANCH_ID_REGEX = /^[A-Za-z0-9]{1,10}$/;
export const PAYMENT_MESSAGE_REGEX = /^[A-Za-z0-9 ]{1,40}$/; // Allows spaces

export const isValidBankAccount = (value: string): boolean => {
  if (!value) return false;

  return (
    NUMERIC_ACCOUNT_REGEX.test(value) ||
    ALPHANUMERIC_ACCOUNT_REGEX.test(value) ||
    OPTIONAL_SEPARATORS_ACCOUNT_REGEX.test(value)
  );
};

export const isValidAba = (value: string): boolean => (value ? ABA_REGEX.test(value) : false);
export const isValidRoutingNumber = (value: string): boolean => (value ? ROUTING_REGEX.test(value) : false);

/**
 * LPC-10289: Validates ABA routing number using the check digit algorithm.
 * This is the same validation C2DS performs.
 *
 * The checksum formula: 3*(d1+d4+d7) + 7*(d2+d5+d8) + 1*(d3+d6+d9) must be divisible by 10
 *
 * @param value - The 9-digit routing number to validate
 * @returns True if the routing number has a valid check digit
 */
export const isValidRoutingNumberCheckDigit = (value: string): boolean => {
  // Must be exactly 9 digits
  if (!value || !/^\d{9}$/.test(value)) return false;

  const digits = value.split("").map(Number);

  const checksum =
    3 * (digits[0] + digits[3] + digits[6]) +
    7 * (digits[1] + digits[4] + digits[7]) +
    1 * (digits[2] + digits[5] + digits[8]);

  return checksum % 10 === 0;
};
// Routing number validation for External funds: alphanumeric, max 15 characters OR exactly 9 digits
export const isValidExternalRoutingNumber = (value: string): boolean => {
  if (!value) return false;

  // Accept either: exactly 9 digits OR alphanumeric up to 15 characters
  return /^\d{9}$/.test(value) || EXTERNAL_ROUTING_REGEX.test(value);
};

// Beneficiary Bank Account # must be at least 9 characters: either 9–18 digits or 9–10 alphanumeric
export const isValidAccountNumber = (value: string): boolean => {
  if (!value) return false;

  return (
    NUMERIC_ACCOUNT_REGEX.test(value) ||
    ALPHANUMERIC_ACCOUNT_REGEX.test(value) ||
    OPTIONAL_SEPARATORS_ACCOUNT_REGEX.test(value)
  );
};

export const isValidAsciiName = (value: string): boolean => {
  if (!value) return false;

  return ASCII_NAME_REGEX.test(value);
};

// Strip leading whitespace; preserves internal and trailing spaces
export const stripLeadingWhitespace = (value: string): string => (value ? value.replace(/^\s+/, "") : "");

// Remove any characters outside allowed ASCII set and enforce optional max length
export const sanitizeAsciiName = (value: string, maxLength?: number): string => {
  if (!value) return "";

  const cleaned = stripLeadingWhitespace(value.replace(/[^A-Za-z0-9\s]/g, ""));

  // Allow spaces (including trailing while typing), but treat all-spaces as empty
  if (cleaned.trim().length === 0) return "";

  if (typeof maxLength === "number" && maxLength > 0) {
    return cleaned.slice(0, maxLength);
  }

  return cleaned;
};

// Keep only digits, cap to optional length
export const sanitizeDigits = (value: string, maxLength?: number): string => {
  if (!value) return "";

  const cleaned = value.replace(/\D/g, "");

  return typeof maxLength === "number" && maxLength > 0 ? cleaned.slice(0, maxLength) : cleaned;
};

// Keep only ASCII letters and digits, cap to optional length
export const sanitizeAsciiAlnum = (value: string, maxLength?: number): string => {
  if (!value) return "";

  const cleaned = value.replace(/[^A-Za-z0-9]/g, "");

  return typeof maxLength === "number" && maxLength > 0 ? cleaned.slice(0, maxLength) : cleaned;
};

// IR-UI-Revamp_V1: SWIFT field validation functions
export const isValidSwiftId = (value: string): boolean => (value ? SWIFT_ID_REGEX.test(value) : false);
export const isValidIBAN = (value: string): boolean => (value ? IBAN_REGEX.test(value) : false);
export const isValidBankId = (value: string): boolean => (value ? BANK_ID_REGEX.test(value) : false);
export const isValidBranchId = (value: string): boolean => (value ? BRANCH_ID_REGEX.test(value) : false);
export const isValidPaymentMessage = (value: string): boolean => {
  if (!value) return false;
  // Check if it matches the base pattern (alphanumeric + spaces, max 40 chars)
  if (!PAYMENT_MESSAGE_REGEX.test(value)) return false;
  // Check for more than 5 consecutive spaces
  if (/ {6,}/.test(value)) return false;

  return true;
};

// IR-UI-Revamp_V1: SWIFT field sanitization functions
export const sanitizeSwiftId = (value: string): string => sanitizeAsciiAlnum(value, 11);
export const sanitizeIBAN = (value: string): string => sanitizeAsciiAlnum(value, 34);
export const sanitizeBankId = (value: string): string => sanitizeDigits(value, 20);
export const sanitizeBranchId = (value: string): string => sanitizeAsciiAlnum(value, 10);
export const sanitizePaymentMessage = (value: string): string => {
  if (!value) return "";
  // Allow alphanumeric and spaces, remove other characters
  let cleaned = stripLeadingWhitespace(value.replace(/[^A-Za-z0-9 ]/g, ""));

  // Limit consecutive spaces to 5
  while (/ {6,}/.test(cleaned)) {
    cleaned = cleaned.replace(/ {6,}/, "     "); // Replace 6+ spaces with exactly 5
  }

  return cleaned.slice(0, 40);
};

// Keep only printable ASCII characters (prevents non-English scripts), optional max length
export const sanitizePrintableAscii = (value: string, maxLength?: number): string => {
  if (!value) return "";

  const cleaned = value.replace(/[^\x20-\x7E]/g, "");

  return typeof maxLength === "number" && maxLength > 0 ? cleaned.slice(0, maxLength) : cleaned;
};

// Keep only letters, digits, spaces and dashes; enforce maximum number of dashes
export const sanitizeAccountNumberInput = (value: string, maxLength?: number, maxDashes = 10): string => {
  if (!value) return "";

  // Remove all characters except letters, numbers, spaces and '-'
  let cleaned = stripLeadingWhitespace(value.replace(/[^A-Za-z0-9 -]/g, ""));

  // Enforce dash count limit by removing extra dashes from the end-leftover sequence
  const dashMatches = cleaned.match(/-/g);
  const dashCount = dashMatches ? dashMatches.length : 0;

  if (dashCount > maxDashes) {
    let allowed = maxDashes;

    cleaned = cleaned
      .split("")
      .filter((ch) => {
        if (ch === "-") {
          if (allowed > 0) {
            allowed -= 1;

            return true;
          }

          return false;
        }

        return true;
      })
      .join("");
  }

  if (typeof maxLength === "number" && maxLength > 0) {
    return cleaned.slice(0, maxLength);
  }

  return cleaned;
};

// Build dynamic required field paths for UpdateWireInstructions based on payment method and toggles
export const getDynamicRequiredPaths = (state: InvestorRequestState): string[] => {
  if (state.requestType !== InvestorRequestType.UpdateWireInstructions) return [];

  const { customFields } = state;
  const method = customFields.paymentMethod;

  const required: string[] = [];

  if (!method) return required; // base required handled elsewhere

  // Common toggles
  if (customFields.furtherCreditYesNo === "yes") {
    // Only Line 1 is required; Line 2 is optional
    required.push("customFields.furtherCreditLine1");
  }

  // IR-UI-Revamp_V1: Intermediary ABA/SWIFT is required only when "Yes" is selected
  if ((method === "fedwire" || method === "swift") && customFields.intermediaryYesNo === "yes") {
    required.push("customFields.intermediarySwiftOrAba");
  }

  switch (method) {
    case "ach":
      required.push(
        "customFields.beneficiaryAccountName",
        "customFields.bankName",
        "customFields.routingNumber",
        "customFields.bankAccountNumber",
        "customFields.bankCountry"
      );
      break;
    case "fedwire":
      required.push(
        "customFields.beneficiaryAccountName",
        "customFields.bankName",
        "customFields.routingNumber",
        "customFields.bankAccountNumber",
        "customFields.bankCountry"
      );
      break;
    case "swift":
      required.push(
        "customFields.beneficiaryAccountName",
        "customFields.bankCountry",
        "customFields.bankName",
        "customFields.bankAccountNumber",
        // IR-UI-Revamp_V1: Mandatory SWIFT fields (IBAN and BankIdNbr are optional)
        "customFields.SwiftId"
      );
      break;
    default:
      break;
  }

  // Free text is optional for all payment methods

  return required;
};

export const hasValidationErrors = (state: InvestorRequestState, baseRequiredPaths: string[]): boolean => {
  const dynamicRequired = getDynamicRequiredPaths(state);
  const allRequired = [...baseRequiredPaths, ...dynamicRequired];
  const isExpandedWireEnabled = FeatureToggleService.isFeatureEnabled(ToggledFeature.ExpandedWireInstructions);

  if (state.requestType === InvestorRequestType.UpdateWireInstructions && isExpandedWireEnabled) {
    if (state.wireFormStep === "upload") {
      allRequired.push("commonFields.attachment");
    }
  }

  // Check required emptiness (treat whitespace-only strings as empty)
  const hasEmpty = allRequired.some((path) => {
    const val = get(state, path);

    if (
      path === "customFields.bankCountry" &&
      isExpandedWireEnabled &&
      state.customFields.paymentMethod &&
      state.customFields.paymentMethod !== "swift"
    ) {
      return false;
    }

    if (typeof val === "string") {
      return val.trim().length === 0;
    }

    return isEmpty(val);
  });

  // Regex validations (apply only when fields present)
  const { customFields } = state;
  const method = customFields.paymentMethod;
  const fundType = customFields.fundType;

  let hasInvalid = false;

  if (method === "ach" || method === "fedwire") {
    // For ACH External funds, use alphanumeric validation (9 digits OR max 15 chars)
    // For FedWire, always use 9-digit numeric validation
    // For Internal funds, use numeric validation (9 digits)
    if (customFields.routingNumber) {
      let isValidRouting = false;

      if (method === "ach" && fundType === "External") {
        // ACH External: allow 9 digits OR alphanumeric (max 15 chars)
        isValidRouting = isValidExternalRoutingNumber(customFields.routingNumber);
      } else {
        // FedWire (all) or ACH Internal: 9 digits only
        isValidRouting = isValidRoutingNumber(customFields.routingNumber);
      }

      if (!isValidRouting) hasInvalid = true;
    }

    if (customFields.bankAccountNumber && !isValidAccountNumber(customFields.bankAccountNumber)) hasInvalid = true;
  }

  // IR-UI-Revamp_V1: Intermediary ABA / SWIFT validation (only when "Yes" is selected)
  if (
    (method === "fedwire" || method === "swift") &&
    customFields.intermediaryYesNo === "yes" &&
    customFields.intermediarySwiftOrAba
  ) {
    const swiftOrAbaValid = /^[A-Za-z0-9]{1,12}$/.test(customFields.intermediarySwiftOrAba);

    if (!swiftOrAbaValid) hasInvalid = true;
  }

  if (isExpandedWireEnabled && method) {
    if (customFields.beneficiaryAccountName && !isValidAsciiName(customFields.beneficiaryAccountName))
      hasInvalid = true;
    if (customFields.bankName && !isValidAsciiName(customFields.bankName)) hasInvalid = true;
  }

  // IR-UI-Revamp_V1: SWIFT-specific field validations
  if (method === "swift") {
    if (customFields.bankCountry) {
      const selectedCountry = state.countries.data.find(
        (c) => String(c.id) === String(customFields.bankCountry) || String(c.name) === String(customFields.bankCountry)
      );

      if (selectedCountry && selectedCountry.name.toLowerCase() === "united states") {
        hasInvalid = true;
      }
    }

    // SwiftId is mandatory and must be valid
    if (customFields.SwiftId && !isValidSwiftId(customFields.SwiftId)) hasInvalid = true;

    // IBAN is optional, but if provided must be valid
    if (customFields.IBAN && !isValidIBAN(customFields.IBAN)) hasInvalid = true;

    if (customFields.BankIdNbr && !isValidBankId(customFields.BankIdNbr)) hasInvalid = true;

    // BranchId is optional, but if provided must be valid
    if (customFields.BranchId && !isValidBranchId(customFields.BranchId)) hasInvalid = true;
  }

  // IR-UI-Revamp_V1: Intermediary field validations for both SWIFT and FedWire (only when "Yes" is selected)
  if ((method === "fedwire" || method === "swift") && customFields.intermediaryYesNo === "yes") {
    // Intermediary Payment Message is optional, but if provided must be valid
    if (customFields.intermediaryPaymentMessage && !isValidPaymentMessage(customFields.intermediaryPaymentMessage)) {
      hasInvalid = true;
    }

    // Intermediary Routing Number is optional, but if provided must be valid (9 digits)
    if (customFields.intermediaryRoutingNumber && !isValidRoutingNumber(customFields.intermediaryRoutingNumber)) {
      hasInvalid = true;
    }
  }

  return hasEmpty || hasInvalid;
};
